import React, { useState, useEffect } from 'react';
import BackButtonBottom from '../../components/common/BackButtonBottom';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { reportIncident, getComplianceConfig } from '../../services/complianceService';
import { getSeverityBadge } from '../../services/complianceService';
import PhotoUploader from '../../components/PhotoUploader';
import { ACCEPTED_IMAGE_TYPES } from '../../services/storageService';
import toast from 'react-hot-toast';

export default function ReportIncident() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userProfile } = useAuth();
  // Optional pre-fill from active trip
  const prefilledBookingId = location.state?.bookingId || null;
  const prefilledBookingRef = location.state?.bookingReferenceNumber || null;
  const prefilledDriverId = location.state?.driverId || null;
  const prefilledRiderId = location.state?.riderId || null;

  const [types, setTypes] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    type: '',
    description: '',
    occurredAt: new Date().toISOString().slice(0, 16),  // YYYY-MM-DDTHH:MM for datetime-local
    injuriesReported: false,
    medicalAssistanceCalled: false,
    policeCalled: false,
    policeReportNumber: '',
    witnesses: [],
    photos: [],  // [{ url, path }] from PhotoUploader; sent as stagedPhotoUrls
    bookingId: prefilledBookingId,
    bookingReferenceNumber: prefilledBookingRef,
    driverId: prefilledDriverId,
    riderId: prefilledRiderId,
  });
  const [newWitnessName, setNewWitnessName] = useState('');
  const [newWitnessContact, setNewWitnessContact] = useState('');

  useEffect(() => {
    getComplianceConfig()
      .then((c) => setTypes(c.notifiableOccurrenceTypes || {}))
      .catch(() => {});
  }, []);

  const selectedTypeConfig = types[form.type];

  const addWitness = () => {
    if (!newWitnessName.trim()) return;
    setForm({ ...form, witnesses: [...form.witnesses, { name: newWitnessName.trim(), contact: newWitnessContact.trim() }] });
    setNewWitnessName(''); setNewWitnessContact('');
  };

  const removeWitness = (idx) => {
    setForm({ ...form, witnesses: form.witnesses.filter((_, i) => i !== idx) });
  };

  const handleSubmit = async () => {
    if (!form.type) { toast.error('Select an incident type'); return; }
    if (!form.description || form.description.trim().length < 20) {
      toast.error('Description must be at least 20 characters');
      return;
    }
    if (selectedTypeConfig?.requiresPhotos && form.photos.length === 0) {
      toast.error('This incident type requires photographic evidence. Please add at least one photo.');
      return;
    }

    setSubmitting(true);
    try {
      const { photos, ...rest } = form;
      const result = await reportIncident({
        ...rest,
        occurredAt: new Date(form.occurredAt).toISOString(),
        stagedPhotoUrls: photos.map(p => p.url),
      });

      const severity = result.severity;
      if (severity === 'critical') {
        toast.success(`Incident ${result.referenceNumber} reported. Admin notified by SMS+email immediately.`, { duration: 6000 });
      } else if (severity === 'high') {
        toast.success(`Incident ${result.referenceNumber} reported. Admin notified by email.`);
      } else {
        toast.success(`Incident ${result.referenceNumber} reported.`);
      }
      navigate(-1);
    } catch (err) {
      toast.error(err.message || 'Reporting failed');
    }
    setSubmitting(false);
  };

  return (
    <div className="max-w-[700px] mx-auto p-6">
      <div className="mb-4">
        <button onClick={() => navigate(-1)} className="bg-transparent border-0 text-brand-primary font-semibold text-[0.9rem] cursor-pointer p-0">← Back</button>
      </div>

      <h1 className="font-serif mt-0 mb-2" style={{ color: '#dc2626' }}>Report an incident</h1>
      <p className="text-body text-[0.9rem] mb-6">
        Use this form to report any safety incident, collision, injury, or notifiable occurrence.
        If this is an emergency, call <strong>000</strong> first.
      </p>

      {prefilledBookingRef && (
        <div className="p-3 rounded-xl bg-slate-50 mb-4 text-[0.85rem] text-body">
          Linked to booking: <strong>{prefilledBookingRef}</strong>
        </div>
      )}

      <div className="form-group">
        <label className="form-label">Incident type *</label>
        <select className="form-input form-select" value={form.type}
          onChange={(e) => setForm({ ...form, type: e.target.value })}>
          <option value="">Select an incident type...</option>
          {Object.entries(types).map(([key, cfg]) => (
            <option key={key} value={key}>{cfg.label}</option>
          ))}
        </select>
        {selectedTypeConfig && (() => {
          const sev = getSeverityBadge(selectedTypeConfig.severity);
          return (
            <div className="mt-2 flex gap-2 items-center">
              <span className="px-2.5 py-[3px] rounded-full text-[0.75rem] font-bold" style={{ background: sev.bg, color: sev.color }}>
                {sev.label} severity
              </span>
              {selectedTypeConfig.timeframeHours && (
                <span className="text-[0.78rem] text-body">
                  Must be reported to NSW Commissioner within {selectedTypeConfig.timeframeHours}hr
                </span>
              )}
            </div>
          );
        })()}
      </div>

      <div className="form-group">
        <label className="form-label">When did it happen? *</label>
        <input type="datetime-local" className="form-input" value={form.occurredAt}
          onChange={(e) => setForm({ ...form, occurredAt: e.target.value })} />
      </div>

      <div className="form-group">
        <label className="form-label">Describe what happened * (min 20 chars)</label>
        <textarea className="form-input" rows={6}
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          placeholder="Detailed description: what happened, who was involved, what was said/done, vehicle/location details..."
        />
        <div className="text-[0.75rem] text-muted mt-1">
          {form.description.length} characters
        </div>
      </div>

      <div className="form-group">
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={form.injuriesReported}
            onChange={(e) => setForm({ ...form, injuriesReported: e.target.checked })} />
          <span>Injuries reported</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer mt-2">
          <input type="checkbox" checked={form.medicalAssistanceCalled}
            onChange={(e) => setForm({ ...form, medicalAssistanceCalled: e.target.checked })} />
          <span>Medical/ambulance was called</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer mt-2">
          <input type="checkbox" checked={form.policeCalled}
            onChange={(e) => setForm({ ...form, policeCalled: e.target.checked })} />
          <span>Police were called</span>
        </label>
        {form.policeCalled && (
          <input className="form-input mt-2"
            placeholder="Police report or event number (if available)"
            value={form.policeReportNumber}
            onChange={(e) => setForm({ ...form, policeReportNumber: e.target.value })} />
        )}
      </div>

      <div className="form-group">
        <label className="form-label">Witnesses (optional)</label>
        {form.witnesses.map((w, i) => (
          <div key={i} className="p-2 rounded-lg bg-slate-50 mb-1.5 flex justify-between items-center text-[0.85rem]">
            <span>{w.name}{w.contact && ` · ${w.contact}`}</span>
            <button onClick={() => removeWitness(i)} className="bg-none border-0 cursor-pointer font-bold" style={{ color: '#ff6b6b' }}>×</button>
          </div>
        ))}
        <div className="flex gap-1.5">
          <input className="form-input flex-1" placeholder="Name" value={newWitnessName}
            onChange={(e) => setNewWitnessName(e.target.value)} />
          <input className="form-input flex-1" placeholder="Phone/email" value={newWitnessContact}
            onChange={(e) => setNewWitnessContact(e.target.value)} />
          <button onClick={addWitness} className="px-4 py-2 rounded-[10px] border-0 bg-brand-primary text-white cursor-pointer font-semibold">Add</button>
        </div>
      </div>

      {selectedTypeConfig?.requiresPhotos && (
        <div className="p-3 rounded-[10px] mb-3 text-[0.82rem]" style={{ background: '#fef3c7', border: '1px solid #fde68a', color: '#92400e' }}>
          ⚠ This incident type requires photographic evidence. Please add at least one photo below.
        </div>
      )}

      <PhotoUploader
        userId={userProfile?.id}
        value={form.photos}
        onChange={(photos) => setForm({ ...form, photos })}
        maxFiles={10}
        acceptedTypes={ACCEPTED_IMAGE_TYPES}
        label={selectedTypeConfig?.requiresPhotos ? 'Photos (required)' : 'Photos (optional)'}
        required={!!selectedTypeConfig?.requiresPhotos}
        helperText="Up to 10 images. Show damage, location, vehicle positioning, etc."
      />

      <button onClick={handleSubmit} disabled={submitting} className="w-full p-3.5 rounded-xl border-0 text-white cursor-pointer font-bold text-[1rem]"
        style={{ background: '#dc2626' }}>
        {submitting ? 'Reporting...' : 'Submit incident report'}
      </button>
      <BackButtonBottom label="Back" />
    </div>
  );
}